import { useHistory } from 'react-router';

import { ApplyButton, CTAContainer } from './styles';
import { JobViewTitleButtonsProps } from '../types';
import { PrimaryButton, SecondaryButton } from 'sharedComponents/Buttons';
import { activeStatusByValue, roleStatusByValue } from 'utils/enums';
import auth from 'utils/auth';
import CreateOutlinedIcon from '@mui/icons-material/CreateOutlined';
import LocalPrintshopOutlinedIcon from '@mui/icons-material/LocalPrintshopOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SendIcon from '@mui/icons-material/Send';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import { ATSJobViewDataTestIds } from '../../../data-testids/ATS';
import { useTheme } from '@mui/material';
import {
  primaryButtonOverrideSx,
  secondaryButtonOverrideSx,
} from 'sharedComponents/Buttons/styles';
import { useState } from 'react';
import { JobViewApplyModal } from './JobViewApplyModal';
import { ApplyLinkStringType, classifyStringType } from 'utils/text';

export const JobViewTitleButtons: React.FC<JobViewTitleButtonsProps> = ({
  handleOpenShareModal,
  handleOpenReviewModal,
  handleOpenRequestModal,
  isPreview,
  isInternalCandidate,
  job,
  onSubmit,
  userDistrict,
  isPublicJobView,
}) => {
  const theme = useTheme();
  const history = useHistory();
  const [isApplyModalOpen, setIsApplyModalOpen] = useState(false);

  const jobDistrict = job?.district?.id;
  const shouldShowEditButton = jobDistrict === userDistrict;
  const jobIsOldJobViewJobAndVisibleToCandidates = 'link_to_ats' in job && isPublicJobView;
  const isPostingOnlyAndVisibleToCandidates = jobIsOldJobViewJobAndVisibleToCandidates
    ? job?.district?.posting_only
    : false;
  const isDistrictAdminAndJobIsPending =
    auth.isDistrictAdmin() && job?.status === roleStatusByValue.submitted;

  const isPending = job?.status === roleStatusByValue.submitted;
  const isSchoolAdminWithPendingJob =
    auth.isSchoolAdmin() &&
    job?.status === roleStatusByValue.submitted &&
    'schoolId' in job &&
    Boolean(job.schoolId);

  const backToJobEdit = () => {
    if (isSchoolAdminWithPendingJob) {
      history.push(`/school/jobedit/${job.schoolId}`);
    } else {
      history.push(`/district/jobedit/${job?.id}`);
    }
  };
  const isSchoolAdmin = auth.isSchoolAdmin();
  const showSocialMediaShareButton =
    job?.status === roleStatusByValue.active &&
    job?.active_status === activeStatusByValue.activeOpen &&
    !auth.inPostingOnlyDistrict();

  const handleOnClickForApplyButtonPostingOnlyDistrict = (stringType: ApplyLinkStringType) => {
    if (stringType !== ApplyLinkStringType.URL) {
      setIsApplyModalOpen(true);
    }
  };

  const handleEditClick = () => {
    if (job.status === roleStatusByValue.active && auth.isDistrictAdmin()) {
      return handleOpenReviewModal();
    } else {
      return backToJobEdit();
    }
  };

  if (isPreview) {
    const postButtonText = auth.isDistrictUserOnly() ? 'Submit to Admin' : 'Post Job';

    return (
      <CTAContainer>
        <SecondaryButton
          dataTestId="back-to-edit-button"
          onClick={backToJobEdit}
          sx={secondaryButtonOverrideSx(theme)}
          startIcon={<CreateOutlinedIcon />}
        >
          Back to Edit
        </SecondaryButton>
        <SecondaryButton
          dataTestId="print-button"
          onClick={() => window.print()}
          startIcon={<LocalPrintshopOutlinedIcon />}
          sx={secondaryButtonOverrideSx(theme)}
        >
          Print
        </SecondaryButton>
        <PrimaryButton
          dataTestId={ATSJobViewDataTestIds.POST_BUTTON}
          onClick={onSubmit}
          startIcon={<SendIcon />}
          sx={primaryButtonOverrideSx(theme)}
        >
          {postButtonText}
        </PrimaryButton>
      </CTAContainer>
    );
  }

  if (isDistrictAdminAndJobIsPending) {
    return (
      <>
        {shouldShowEditButton && (
          <CTAContainer>
            <SecondaryButton
              dataTestId="print-button"
              onClick={() => window.print()}
              startIcon={<LocalPrintshopOutlinedIcon />}
              sx={secondaryButtonOverrideSx(theme)}
            >
              Print
            </SecondaryButton>
            <PrimaryButton
              dataTestId={ATSJobViewDataTestIds.REVIEW_REQUEST_BUTTON}
              onClick={handleOpenRequestModal}
              startIcon={<CreateOutlinedIcon />}
              sx={primaryButtonOverrideSx(theme)}
            >
              Review request
            </PrimaryButton>
          </CTAContainer>
        )}
      </>
    );
  }

  if (isPending && !auth.isDistrictAdmin()) {
    return (
      <>
        {shouldShowEditButton && (
          <CTAContainer>
            <SecondaryButton
              dataTestId="print-button"
              onClick={() => window.print()}
              startIcon={<LocalPrintshopOutlinedIcon />}
              sx={secondaryButtonOverrideSx(theme)}
            >
              Print
            </SecondaryButton>
            <PrimaryButton
              dataTestId={ATSJobViewDataTestIds.EDIT_BUTTON}
              onClick={handleEditClick}
              startIcon={<CreateOutlinedIcon />}
              sx={primaryButtonOverrideSx(theme)}
            >
              Edit Posting
            </PrimaryButton>
          </CTAContainer>
        )}
      </>
    );
  } else if (!isPending && auth.isDistrictAdmin()) {
    return (
      <>
        {shouldShowEditButton && (
          <CTAContainer>
            {showSocialMediaShareButton && (
              <SecondaryButton
                dataTestId="share-button"
                onClick={() => handleOpenShareModal()}
                startIcon={<ShareOutlinedIcon />}
                sx={secondaryButtonOverrideSx(theme)}
              >
                Share
              </SecondaryButton>
            )}
            <SecondaryButton
              dataTestId="print-button"
              onClick={() => window.print()}
              startIcon={<LocalPrintshopOutlinedIcon />}
              sx={secondaryButtonOverrideSx(theme)}
            >
              Print
            </SecondaryButton>
            <PrimaryButton
              dataTestId={ATSJobViewDataTestIds.EDIT_BUTTON}
              onClick={handleEditClick}
              startIcon={<CreateOutlinedIcon />}
              sx={primaryButtonOverrideSx(theme)}
            >
              Edit Posting
            </PrimaryButton>
          </CTAContainer>
        )}
      </>
    );
  } else if (isSchoolAdmin || auth.isDistrictUserOnly()) {
    return (
      <CTAContainer>
        {showSocialMediaShareButton && (
          <SecondaryButton
            dataTestId="share-button"
            onClick={() => handleOpenShareModal()}
            startIcon={<ShareOutlinedIcon />}
            sx={secondaryButtonOverrideSx(theme)}
          >
            Share
          </SecondaryButton>
        )}
        <SecondaryButton
          dataTestId="print-button"
          onClick={() => window.print()}
          startIcon={<LocalPrintshopOutlinedIcon />}
          sx={secondaryButtonOverrideSx(theme)}
        >
          Print
        </SecondaryButton>
      </CTAContainer>
    );
  } else if (isPostingOnlyAndVisibleToCandidates) {
    const linkToApply: string = jobIsOldJobViewJobAndVisibleToCandidates ? job.link_to_ats : '';
    const stringType = classifyStringType(linkToApply);

    return (
      <>
        <a
          href={stringType === ApplyLinkStringType.URL ? linkToApply : null}
          data-testid={ATSJobViewDataTestIds.APPLY_LINK}
        >
          <ApplyButton
            data-testid={ATSJobViewDataTestIds.APPLY_BUTTON}
            variant="contained"
            onClick={() => handleOnClickForApplyButtonPostingOnlyDistrict(stringType)}
          >
            Apply
          </ApplyButton>
        </a>
        <JobViewApplyModal
          text={linkToApply}
          open={isApplyModalOpen}
          onClose={() => setIsApplyModalOpen(false)}
        />
      </>
    );
  } else if (jobIsOldJobViewJobAndVisibleToCandidates) {
    const urlParams = new URLSearchParams(window.location.search);
    if (isInternalCandidate) {
      urlParams.set('internal', window.btoa('true'));
    }
    const districtRole = job?.district_role;
    const roleId = window.location.search.indexOf('is_school_role=1') > -1 ? districtRole : job?.id;

    return (
      <CTAContainer>
        <ApplyButton
          data-testid={ATSJobViewDataTestIds.APPLY_BUTTON}
          onClick={() => history.push(`/applicant-profile/${roleId}/?${urlParams.toString()}`)}
          startIcon={<OpenInNewIcon />}
          variant="contained"
        >
          Apply
        </ApplyButton>
      </CTAContainer>
    );
  } else {
    return <></>;
  }
};
